/**
 * The damaged Otto-pen mouth: a broken precast corner and its exposed rebar.
 *
 * One pen in the fattening hall has had the bottom of its alley-side corner
 * smashed out at the kerb, where the scraper has clipped it for years. It is
 * 0.50 m across and 0.52 m tall, which Rosalie walks through and Otto — 1.06 m
 * at the shoulder — could not get his head into. **Nothing in beat 3 says that
 * out loud.** Spec §3's *"cannot escape, too big"* stops being an assertion the
 * moment the player has walked through the reason.
 *
 * The piece that came out is `pen-rubble`, placed beside it by the room. It is
 * not in here; see this asset's `footprint`.
 */
import * as THREE from 'three';
import { surface } from '../materials';
import type { AssetDefinition } from '../types';
import { getZone } from '@/render/palette';

function fatteningSurfaces(): readonly [number, number, number, number, number] {
  const surfaces = getZone('fattening')?.surfaces;
  if (!surfaces || surfaces.length < 5) throw new Error('fattening zone needs five surface colours');
  return [surfaces[0]!, surfaces[1]!, surfaces[2]!, surfaces[3]!, surfaces[4]!];
}

function box(
  size: readonly [number, number, number],
  colour: number,
  name: string,
): THREE.Mesh {
  const mesh = new THREE.Mesh(new THREE.BoxGeometry(...size), surface('mineral', colour));
  mesh.name = name;
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

function rail(radius: number, length: number, colour: number, name: string): THREE.Mesh {
  const mesh = new THREE.Mesh(new THREE.CylinderGeometry(radius, radius, length, 6), surface('metal', colour));
  mesh.name = name;
  mesh.castShadow = true;
  return mesh;
}

function build(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'pen-wall-broken';
  const [shadow, concrete, steel] = fatteningSurfaces();

  // The lower casting ends at the failed corner. Keeping its concrete run whole
  // is what leaves a 0.50 m hole rather than turning the mouth into a lowered wall.
  const lowerPanel = box([0.1, 0.3725, 1.78], concrete, 'precast-panel-lower');
  lowerPanel.position.set(0, 0.18625, 0.31);
  const castingSeam = box([0.07, 0.015, 1.78], shadow, 'recessed-casting-seam');
  castingSeam.position.set(0, 0.38, 0.31);
  const upperPanel = box([0.1, 0.3625, 1.78], concrete, 'precast-panel-upper');
  upperPanel.position.set(0, 0.56875, 0.31);
  group.add(lowerPanel, castingSeam, upperPanel);

  // ── The lintel is three courses and not one, and that is the whole read ─────
  //
  // A single box over the opening gave a clean rectangular notch: plumb on one
  // side, level on top, and it looked like a panel section somebody had lifted
  // out — which is the hospital pen's story two metres away, not this one.
  //
  // Concrete does not fail in rectangles. A corner spall is **widest at the
  // kerb and tapers upward**, because that is where the scraper hits and where
  // the water gets in, so each course above the hole reaches further back
  // toward the post than the one below it.
  //
  // All three sit at or above `OTTO_GAP.top`, so none of them is in her way.
  // The collider over this span is a single box and therefore solid where these
  // courses leave the picture open — see `fatteningSolids`, which says how far
  // that disagreement runs. Safe direction, but not fidelity.
  // Each course runs back from the surviving panel at −0.58 toward the post, and
  // the lowest reaches least far — so what is missing is deepest **at the post**,
  // which is the corner that failed.
  for (const [backZ, fromY, toY, name] of [
    [-1.2, 0.66, 0.75, 'lintel-course-upper'],
    [-0.9, 0.59, 0.66, 'lintel-course-middle'],
    [-0.72, 0.52, 0.59, 'lintel-course-lower'],
  ] as const) {
    const course = box([0.1, toY - fromY, -0.58 - backZ], concrete, name);
    course.position.set(0, (fromY + toY) / 2, (backZ - 0.58) / 2);
    group.add(course);
  }

  const coping = box([0.12, 0.04, 2.4], concrete, 'proud-coping');
  coping.position.y = 0.73;
  group.add(coping);

  for (const [z, name] of [[-1.14, 'end-post-back'], [1.14, 'end-post-front']] as const) {
    const post = box([0.12, 1.05, 0.12], concrete, name);
    post.position.set(0, 0.525, z);
    group.add(post);
  }

  for (const [y, name] of [[0.9, 'rail-low'], [1.04, 'rail-top']] as const) {
    const horizontal = rail(0.02, 2.4, steel, name);
    horizontal.rotation.x = Math.PI / 2;
    horizontal.position.y = y;
    group.add(horizontal);
  }
  for (const [z, name] of [[-1.14, 'upright-back'], [0, 'upright-centre'], [1.14, 'upright-front']] as const) {
    const upright = rail(0.02, 1.05, steel, name);
    upright.position.set(0, 0.525, z);
    group.add(upright);
  }

  // Spalled surface along the failed edge, proud of the 0.10 m casting so it
  // catches the sodium differently from the flat panel beside it.
  //
  // **Everything here sits on the panel side of the break line**, never inside
  // the opening. `OTTO_GAP` is what the collider is cut to, and a lump of
  // concrete hanging 40 mm into it would be a wall she can see through and
  // cannot walk through — the exact failure the three-box mouth exists to
  // prevent, reintroduced as decoration.
  for (const [size, y, z, name] of [
    [[0.13, 0.075, 0.05], 0.1075, -0.552, 'break-face-low'],
    [[0.125, 0.06, 0.07], 0.2825, -0.542, 'break-face-mid'],
    [[0.12, 0.05, 0.055], 0.4275, -0.55, 'break-face-high'],
    [[0.12, 0.045, 0.09], 0.5, -0.523, 'break-face-shoulder'],
  ] as const) {
    const chip = box(size, concrete, name);
    chip.position.set(0, y, z);
    group.add(chip);
  }

  // Rebar begins above the clearance line: anything lower would turn the proof
  // that she fits through the opening into an invisible controller obstruction.
  for (const [y, tilt, name] of [[0.475, 0.025, 'exposed-rebar-low'], [0.498, -0.025, 'exposed-rebar-high']] as const) {
    const rebar = rail(0.006, 0.46, steel, name);
    rebar.rotation.set(Math.PI / 2 + tilt, 0.07, 0);
    rebar.position.set(0, y, -0.83);
    group.add(rebar);
  }

  return group;
}

export const penWallBrokenAsset: AssetDefinition = {
  id: 'pen-wall-broken',
  label: 'Fattening pen wall, broken corner',
  description: 'A precast pen wall whose bottom corner has been broken out, with exposed rebar.',
  // 0.12, the END POSTS, exactly as `pen-wall` declares — this is the same
  // casting with a corner missing and it aligns with its neighbours the same way.
  //
  // **The fallen slab used to be in here and that is why this number was 0.48.**
  // An asset must measure centred on its own origin (`asset.spec`, 20 mm), and a
  // wall module with debris heaped on one side of it does not. See `pen-rubble`.
  footprint: { width: 0.12, height: 1.06, length: 2.4 },
  create: build,
};
